/**
 * Invite User Form Composer
 *
 * Sends (or edits) an invitation via `upsertInvitation` (POST /uniq/invitations).
 * - "Invite as" lists active invitation types (getInvitationTypes?status=active); the
 *   selected type's targetKind decides whether an organization target is needed.
 * - Organizations are limited to the caller's active memberships — the server rejects
 *   any other org with 403 INVITATION_TARGET_FORBIDDEN.
 * - Edit mode (`invitation` set): only names, email and mobile may change; type and target
 *   are read-only. Edit sends nothing, so after an email/mobile change we offer a resend.
 * - A 409 "already pending" offers to resend the existing invitation instead.
 *
 * The backend returns human-readable messages (not codes), so errors are mapped by text.
 */

import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import { InviteUserFormSchema, transformToApiRequest, FORM_CONFIG, type InviteUserFormData } from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { renderTextControl, renderSelectControl, renderButtonControl } from '@/forms/formRenderFunctions'
import { PhoneInputField } from '@/components/ui/phone-input'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/contexts/ToastContext'
import { loadUserInfo } from '@/security/routeSecurityVerifier'
import { AppLogger } from '@/lib/logger'
import { Send } from 'lucide-react'
import type { Invitation, InvitationType } from '@/types/users'

const log = AppLogger.getLogger(import.meta)

export type InviteUserComposerProps = {
  /** Edit an existing pending invitation instead of creating one. */
  invitation?: Invitation
  /** Display name of the edited invitation's target (edit mode only). */
  targetLabel?: string
  onSuccess?: () => void
  onCancel?: () => void
  showHeader?: boolean
}

type FieldName = keyof InviteUserFormData
const control = (name: FieldName) => FormConfigLoader.getControl(FORM_CONFIG, name)

export function InviteUserComposer({ invitation, targetLabel, onSuccess, onCancel, showHeader = true }: InviteUserComposerProps) {
  const { toast } = useToast()
  const isEdit = !!invitation
  const callerUserNumber = React.useMemo(() => loadUserInfo()?.userNumber, [])

  // Edit mode may reference a type that's since been deactivated/archived — fetch them all for its label.
  const { data: typesData, refetch: refetchTypes } = useApiQuery(
    'getInvitationTypes', isEdit ? { includeArchived: 'true' } : { status: 'active' }, {},
  )
  const types: InvitationType[] = (typesData as any)?.content?.invitationTypes ?? []

  const { data: membershipsData, isLoading: orgsLoading } = useApiQuery(
    'getMemberships', { userNumber: callerUserNumber }, { enabled: !isEdit && !!callerUserNumber },
  )
  const orgOptions = isEdit
    ? invitation?.targetReference ? [{ value: invitation.targetReference, label: targetLabel ?? invitation.targetReference }] : []
    : (((membershipsData as any)?.content?.memberships ?? []) as any[])
        .filter((m) => m.status === 'active' && !m.archiveDate)
        .map((m) => ({ value: String(m.organization.orgNumber), label: m.organization.name as string }))

  const upsert = useApiMutation('upsertInvitation', 'POST', { invalidates: ['getInvitationList'] })
  const resend = useApiMutation('resendInvitation', 'POST', { invalidates: ['getInvitationList'] })
  // GET as a mutation so the lookup can be parameterised at call time (409 → find the pending one).
  const lookup = useApiMutation('getInvitationList', 'GET')

  const [conflict, setConflict] = React.useState<InviteUserFormData | null>(null)
  const [resendPrompt, setResendPrompt] = React.useState<{ invitationNumber: number; email: string } | null>(null)

  const form = useAppForm({
    schema: InviteUserFormSchema,
    defaultValues: {
      invitationType: invitation?.invitationType ?? '',
      targetKind: invitation?.targetKind,
      targetReference: invitation?.targetReference ?? '',
      firstName: invitation?.firstName ?? '',
      lastName: invitation?.lastName ?? '',
      emailId: invitation?.emailId ?? '',
      mobileNumber: invitation?.mobileNumber ?? '',
    } as InviteUserFormData,
    onSubmit: async ({ value }) => {
      const parsed = InviteUserFormSchema.safeParse(value)
      if (!parsed.success) return
      const data = parsed.data

      try {
        await upsert.mutateAsync(transformToApiRequest(data, invitation?.invitationNumber))
      } catch (error) {
        handleServerError((error as Error)?.message ?? '', data)
        return
      }

      if (!invitation) {
        toast({ title: 'Invitation sent', description: `Invitation sent to ${data.emailId}.`, type: 'success' })
        onSuccess?.()
        return
      }

      toast({ title: 'Invitation updated', type: 'success' })
      const contactChanged =
        data.emailId.toLowerCase() !== invitation.emailId.toLowerCase() ||
        (data.mobileNumber || null) !== (invitation.mobileNumber || null)
      if (contactChanged) setResendPrompt({ invitationNumber: invitation.invitationNumber, email: data.emailId })
      else onSuccess?.()
    },
  })

  const setFieldError = (name: FieldName, message: string) =>
    form.setFieldMeta(name, (meta) => ({ ...meta, isTouched: true, errors: [message] }))

  const handleServerError = (msg: string, data: InviteUserFormData) => {
    log.error('[InviteUserComposer] upsertInvitation failed:', msg)
    if (/already.*pending|ALREADY_PENDING/i.test(msg)) return setConflict(data)
    if (/type is unknown|TYPE_INVALID/i.test(msg)) {
      toast({ title: 'Invitation type unavailable', description: 'This invitation type is no longer available.', type: 'error' })
      form.setFieldValue('invitationType', '')
      refetchTypes()
      return
    }
    if (/can't invite|TARGET_FORBIDDEN/i.test(msg)) {
      return toast({ title: 'Not allowed', description: "You can only invite people to organizations you're an active member of.", type: 'error' })
    }
    if (/target was not found|TARGET_NOT_FOUND/i.test(msg)) {
      return toast({ title: 'Not found', description: 'That organization could not be found.', type: 'error' })
    }
    if (/target/i.test(msg)) return setFieldError('targetReference', msg)
    if (/mobile/i.test(msg)) return setFieldError('mobileNumber', msg)
    if (/email/i.test(msg)) return setFieldError('emailId', msg)
    if (/first name/i.test(msg)) return setFieldError('firstName', msg)
    if (/last name/i.test(msg)) return setFieldError('lastName', msg)
    toast({ title: isEdit ? 'Could not update invitation' : 'Could not send invitation', description: msg || 'Please try again.', type: 'error' })
  }

  const doResend = async (invitationNumber: number, email: string) => {
    try {
      await resend.mutateAsync({ invitationNumber })
      toast({ title: 'Invitation resent', description: `Invitation resent to ${email}.`, type: 'success' })
      onSuccess?.()
    } catch (error) {
      toast({ title: 'Could not resend invitation', description: (error as Error)?.message, type: 'error' })
    }
  }

  const resendConflicting = async () => {
    if (!conflict) return
    const data = conflict
    setConflict(null)
    const target = data.targetKind === 'none' ? null : data.targetReference || null
    try {
      const res: any = await lookup.mutateAsync({
        status: 'pending',
        invitationType: data.invitationType,
        search: data.emailId,
        ...(target && { targetReference: target }),
      })
      const match = ((res?.content?.invitations ?? []) as Invitation[]).find(
        (i) => i.status === 'pending' &&
          i.emailId.toLowerCase() === data.emailId.toLowerCase() &&
          i.invitationType === data.invitationType &&
          (i.targetReference ?? null) === target,
      )
      if (!match) {
        toast({ title: "Couldn't find the pending invitation", description: 'Resend it from the Invitations tab.', type: 'error' })
        return
      }
      await doResend(match.invitationNumber, data.emailId)
    } catch (error) {
      toast({ title: 'Could not resend invitation', description: (error as Error)?.message, type: 'error' })
    }
  }

  const onTypeChange = (name: string) => {
    form.setFieldValue('targetKind', types.find((t) => t.invitationTypeName === name)?.targetKind)
    form.setFieldValue('targetReference', '')
  }

  const selectedTypeName = form.useStore((s) => s.values.invitationType)
  const targetKind = form.useStore((s) => s.values.targetKind)
  const selectedType = types.find((t) => t.invitationTypeName === selectedTypeName)

  const typeOptions = types.map((t) => ({ value: t.invitationTypeName, label: t.invitationTypeLabel }))
  if (isEdit && invitation && !typeOptions.some((o) => o.value === invitation.invitationType)) {
    typeOptions.push({ value: invitation.invitationType, label: invitation.invitationType })
  }

  const textField = (name: FieldName, type?: 'text' | 'email') => {
    const c = control(name)
    return (
      <form.AppField key={name} name={name}>
        {(bound) => renderTextControl({ name, label: c?.label ?? name, placeholder: c?.placeholder, required: true, type }, bound)}
      </form.AppField>
    )
  }

  const formBody = (
    <>
      <div className="space-y-4">
        <form.AppField name="invitationType" listeners={{ onChange: ({ value }) => onTypeChange(value as string) }}>
          {(bound) => renderSelectControl({
            name: 'invitationType',
            label: control('invitationType')?.label ?? 'Invite as',
            placeholder: types.length === 0 && !isEdit ? 'No active invitation types' : control('invitationType')?.placeholder,
            required: true,
            disabled: isEdit,
            options: typeOptions,
            description: selectedType?.invitationTypeDescription ?? (isEdit ? 'Type can’t change — revoke and invite again instead.' : undefined),
          }, bound)}
        </form.AppField>

        {targetKind === 'organization' && (
          <form.AppField name="targetReference">
            {(bound) => renderSelectControl({
              name: 'targetReference',
              label: control('targetReference')?.label ?? 'Organization',
              placeholder: !isEdit && !orgsLoading && orgOptions.length === 0
                ? 'You have no active organization memberships'
                : control('targetReference')?.placeholder,
              required: true,
              disabled: isEdit,
              isLoading: orgsLoading,
              options: orgOptions,
              description: isEdit ? 'Target can’t change — revoke and invite again instead.' : 'Only organizations you are an active member of.',
            }, bound)}
          </form.AppField>
        )}
      </div>

      <div className="space-y-4">
        {textField('firstName')}
        {textField('lastName')}
        {textField('emailId', 'email')}
        <form.AppField name="mobileNumber">
          {(bound) => (
            <PhoneInputField
              label={control('mobileNumber')?.label ?? 'Mobile Number'}
              value={bound.state.value as string | undefined}
              onChange={(val) => bound.handleChange(val ?? '')}
              onBlur={bound.handleBlur}
              error={bound.state.meta.errors?.[0] as string | undefined}
            />
          )}
        </form.AppField>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && renderButtonControl({ label: FORM_CONFIG.cancelButton?.label ?? 'Cancel', variant: 'outline', type: 'button', onClick: onCancel })}
        {renderButtonControl(
          { label: (isEdit ? FORM_CONFIG.editSubmitButton?.label : FORM_CONFIG.submitButton?.label) ?? 'Send Invite', type: 'submit' },
          { SubmitButton: form.SubmitButton },
        )}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Send className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{isEdit ? 'Edit Invitation' : FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}

      <Dialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Already invited</DialogTitle>
            <DialogDescription>{conflict?.emailId} already has a pending invitation for this. Resend it instead?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflict(null)}>Cancel</Button>
            <Button onClick={resendConflicting} disabled={lookup.isPending || resend.isPending}>Resend</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resendPrompt} onOpenChange={(o) => { if (!o) { setResendPrompt(null); onSuccess?.() } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resend the invitation?</DialogTitle>
            <DialogDescription>Saving didn't send anything. Resend the invitation to the new address?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResendPrompt(null); onSuccess?.() }}>Not now</Button>
            <Button
              onClick={() => { const p = resendPrompt!; setResendPrompt(null); doResend(p.invitationNumber, p.email) }}
              disabled={resend.isPending}
            >
              Resend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form.AppForm>
  )
}
